import { useGLTF } from "@react-three/drei";
import { useMemo, useEffect } from "react";
import * as THREE from "three";
import { DoubleSide } from "three";
import GlbLoader from "../../Utils/GlbLoader";
import { assignMaterialToObjects } from "../../Utils/AssignMaterialToObject";
import { stateMemory, stateContentReady } from "../../store";
import { useSnapshot } from "valtio";

export default function SalzabbauRoomShell({ visible }) {
  const { blueGlasClear, ParkettColor, ParkettNormal, ParkettRough, imperfectionRoughMap } = GlbLoader();
  const snapStateMemory = useSnapshot(stateMemory);
  const contentReady = useSnapshot(stateContentReady)[3];
  const gltf = useGLTF("../models/Museum01/Showroom.glb");

  // Materials created and assigned synchronously — room renders dark on first frame, no white flash.
  const mats = useMemo(() => {
    ParkettColor.repeat.set(20, 20);
    ParkettNormal.repeat.set(20, 20);
    ParkettRough.repeat.set(20, 20);

    const m = {
      blackMattMainRestMaterial:    new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#343333", roughnessMap: imperfectionRoughMap }),
      blackMattMainWallsMaterial:   new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#343333", roughnessMap: imperfectionRoughMap }),
      blackMattMainFloorAOMaterial: new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#0C0C0C", roughnessMap: imperfectionRoughMap }),
      blackMattMaterial:            new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#181818", roughnessMap: imperfectionRoughMap }),
      blueMattMaterial:             new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#269EBE" }),
      blackGlossyRestMaterial:      new THREE.MeshStandardMaterial({ roughness: 0.1, color: "#181818", metalness: 0, roughnessMap: imperfectionRoughMap }),
      blackGlossyWallMaterial:      new THREE.MeshStandardMaterial({ roughness: 0.1, color: "#181818", metalness: 0, roughnessMap: imperfectionRoughMap }),
      parkettMaterial:              new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0, map: ParkettColor, roughnessMap: ParkettRough, normalMap: ParkettNormal }),
      orangeGlossyWall:             new THREE.MeshStandardMaterial({ roughness: 0,   metalness: 0, color: "#fc8207" }),
      blackGlasMaterial:            new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 0.1, transparent: true, opacity: 0.85, side: DoubleSide }),
      blackGlasVitrineMaterial:     new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 0.1, transparent: true, opacity: 0.2 }),
      aquariumGlasMaterial:         new THREE.MeshStandardMaterial({ color: "#0092FF", transparent: true, opacity: 0.2, roughness: 0 }),
    };

    assignMaterialToObjects(gltf.scene, ["Object191"], m.blueMattMaterial);
    assignMaterialToObjects(gltf.scene, ["glas01", "Box370"], m.blackGlasMaterial);
    assignMaterialToObjects(gltf.scene, ["Cylinder111"], blueGlasClear);
    assignMaterialToObjects(gltf.scene, ["Parkett"], m.parkettMaterial);
    assignMaterialToObjects(gltf.scene, ["WegweiserBlack", "Dach", "Arrows"], m.blackMattMaterial);
    assignMaterialToObjects(gltf.scene, ["WegweiserBlack", "Room_BronzezeitWalls", "TalgLampePodest002"], m.blackMattMainRestMaterial);
    assignMaterialToObjects(gltf.scene, ["FloorBlack", "GangFloor"], m.blackMattMainFloorAOMaterial);
    assignMaterialToObjects(gltf.scene, ["WallsRoom", "MiddleWall"], m.blackMattMainWallsMaterial);
    assignMaterialToObjects(gltf.scene, ["Sesselleiste"], m.blackGlossyWallMaterial);
    assignMaterialToObjects(gltf.scene, ["BlackGlossy"], m.blackGlossyRestMaterial);
    assignMaterialToObjects(gltf.scene, ["WegWeiserOrange", "OrangeLines"], m.orangeGlossyWall);

    return m;
  }, [gltf.scene]);

  // Textures stream in only after room opens AND content GLBs are ready.
  useEffect(() => {
    if (!visible || !contentReady) return;

    const base = snapStateMemory.memoryLowDevice
      ? "../overallTextures/Salzabbau/LOW/"
      : "../overallTextures/Salzabbau/";

    let active = true;
    const loadedTextures = [];

    const applyAO = (tex, ...targets) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.channel = 1;
      loadedTextures.push(tex);
      targets.forEach((m) => { m.aoMap = tex; m.aoMapIntensity = 0.8; m.needsUpdate = true; });
    };

    const loader = new THREE.TextureLoader();
    loader.load(base + "BAKE_MainWallsAO.webp",  (tex) => applyAO(tex, mats.blackMattMainWallsMaterial, mats.blackGlossyWallMaterial));
    loader.load(base + "BAKE_MainFloorAO.webp",  (tex) => applyAO(tex, mats.blackMattMainFloorAOMaterial));
    loader.load(base + "BAKE_RestAO.webp",       (tex) => applyAO(tex, mats.blackMattMainRestMaterial, mats.blackGlossyRestMaterial, mats.parkettMaterial));

    return () => {
      active = false;
      loadedTextures.forEach((tex) => tex.dispose());
      [
        mats.blackMattMainWallsMaterial, mats.blackGlossyWallMaterial,
        mats.blackMattMainFloorAOMaterial, mats.blackMattMainRestMaterial,
        mats.blackGlossyRestMaterial, mats.parkettMaterial,
      ].forEach((m) => { m.aoMap = null; m.needsUpdate = true; });
    };
  }, [visible, contentReady]);

  return <primitive object={gltf.scene} visible={visible} />;
}
